/**
 * Error Boundary Component
 *
 * F10: React Error Boundary with Sentry integration
 *
 * Features:
 * - Catches all React errors in child components
 * - Automatically reports errors to Sentry
 * - Shows user-friendly error UI
 * - Allows users to report additional context
 * - Provides recovery options (reload, go home)
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */

import { Component, ReactNode, ErrorInfo } from 'react'
import * as Sentry from '@sentry/react'
import { Button } from './Button'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Custom fallback UI (optional) */
  fallback?: ReactNode
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  eventId: string | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)

    // Report to Sentry
    Sentry.withScope((scope) => {
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
      })

      const eventId = Sentry.captureException(error)

      this.setState({
        errorInfo,
        eventId,
      })
    })

    // Call custom error handler
    this.props.onError?.(error, errorInfo)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  handleReportFeedback = () => {
    const { eventId } = this.state

    if (eventId) {
      // Open Sentry user feedback dialog
      Sentry.showReportDialog({ eventId })
    }
  }

  render() {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback
      }

      // Default error UI
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--spacing-2xl)',
            backgroundColor: 'var(--color-background)',
          }}
        >
          <div
            style={{
              maxWidth: '600px',
              textAlign: 'center',
            }}
          >
            {/* Error Icon */}
            <div
              style={{
                fontSize: '64px',
                marginBottom: 'var(--spacing-lg)',
              }}
              role="img"
              aria-label="Error"
            >
              ⚠️
            </div>

            {/* Error Message */}
            <h1
              style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-md)',
              }}
            >
              Oops! Something went wrong
            </h1>

            <p
              style={{
                fontSize: 'var(--font-size-md)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-xl)',
                lineHeight: 1.6,
              }}
            >
              We've been notified and are working on a fix. In the meantime, try
              reloading the page or returning to the homepage.
            </p>

            {/* Error Details (development only) */}
            {import.meta.env.MODE === 'development' && error && (
              <details
                style={{
                  textAlign: 'left',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-xl)',
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 600,
                    color: 'var(--color-error)',
                    marginBottom: 'var(--spacing-sm)',
                  }}
                >
                  Error Details (Development Only)
                </summary>

                <pre
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    overflowX: 'auto',
                    margin: 0,
                  }}
                >
                  <strong>Error:</strong> {error.message}
                  {'\n\n'}
                  <strong>Stack:</strong>
                  {'\n'}
                  {error.stack}
                  {errorInfo?.componentStack && (
                    <>
                      {'\n\n'}
                      <strong>Component Stack:</strong>
                      {'\n'}
                      {errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-md)',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button variant="primary" size="lg" onClick={this.handleReload}>
                Reload Page
              </Button>

              <Button variant="secondary" size="lg" onClick={this.handleGoHome}>
                Go to Homepage
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={this.handleReportFeedback}
              >
                Report Issue
              </Button>
            </div>

            {/* Event ID for support */}
            {this.state.eventId && (
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-disabled)',
                  marginTop: 'var(--spacing-xl)',
                }}
              >
                Error ID: {this.state.eventId}
              </p>
            )}
          </div>
        </div>
      )
    }

    return children
  }

  static displayName = 'ErrorBoundary'
}

/**
 * Alternative: Functional Error Boundary using Sentry's built-in component
 *
 * @example
 * ```tsx
 * import { SentryErrorBoundary } from './ErrorBoundary'
 *
 * <SentryErrorBoundary fallback={<ErrorUI />}>
 *   <App />
 * </SentryErrorBoundary>
 * ```
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary
